/**
 * ContractorPortalLinkModal — "Manage portal access" for a construction contract (2026-07 —
 * per-recipient access, database/140_contractor_portal_recipients.sql, mirrors
 * SendJourneyAccessModal.jsx one level down).
 *
 * Opens via CustomEvent('open-contractor-portal-link', { detail: { contractId, contractorName } }).
 *
 * Two parts:
 *   1. Recipients — the primary flow. Each person on the GC's team gets their own link + 6-digit
 *      PIN, individually revocable and individually attributable on every RFI/submittal/proposal
 *      response. Sourced from the contract's linked GC Organization's Contacts (contractor_airtable_id
 *      on construction_contracts — see docs/CONTRACTOR_PORTAL.md), or add a name+email manually
 *      for anyone not in Contacts yet. Per-row resend (same link+PIN) / regenerate (rotate just
 *      THEIRS) / revoke (or reactivate); a revoked row also offers "Remove from list".
 *   2. Shared link (legacy) — the original per-CONTRACT token (database/047_contractor_portal.sql)
 *      still works for anyone who already has it, but is never issued to anyone new now that
 *      per-recipient access exists. Kept visible so the team can still disable it entirely or
 *      rotate it away if it's been over-shared.
 */

import { useState, useEffect, useCallback } from 'react'
import { createPortal } from 'react-dom'
import {
  getOrCreatePortal, setPortalStatus, regenerateToken,
  listRecipients, addRecipients, revokeRecipient, reactivateRecipient, deleteRecipient,
  resendRecipientAccess, regenerateRecipientAccess,
} from '../services/contractorPortal.js'
import { getContract } from '../services/bidCampaigns.js'
import { getOrganizationDetail, getContactNamesByIds, getContactEmailsByIds } from '../services/contacts.js'
import { ActionMenu, ActionMenuButton, ActionMenuItem, ActionMenuDivider, useActionMenu } from './ActionMenu.jsx'
import { confirmDialog } from './Dialog.jsx'

const EMAIL_RE = /^[^\s@]+@[^\s@]+\.[^\s@]+$/

const labelStyle = {
  display: 'block', fontFamily: '"DM Mono",monospace', fontSize: 10,
  color: 'var(--aj-text-mid)', letterSpacing: '0.04em', marginBottom: 4, textTransform: 'uppercase',
}
const inputStyle = {
  width: '100%', padding: '7px 10px', fontFamily: '"DM Sans",sans-serif', fontSize: 12,
  color: 'var(--aj-text)', background: 'var(--aj-bg-subtle)', border: '0.5px solid var(--aj-border-mid)',
  borderRadius: 4, boxSizing: 'border-box', outline: 'none',
}
const smallBtn = { padding: '5px 10px', fontFamily: '"DM Sans",sans-serif', fontSize: 11, background: 'none', border: '0.5px solid var(--aj-border-mid)', borderRadius: 4, cursor: 'pointer', color: 'var(--aj-text)', whiteSpace: 'nowrap' }

function fmtWhen(iso) {
  if (!iso) return 'Never opened'
  const d = new Date(iso)
  return 'Opened ' + d.toLocaleDateString(undefined, { month: 'short', day: 'numeric' })
}

export default function ContractorPortalLinkModal() {
  const [open, setOpen]         = useState(false)
  const [contractId, setCid]    = useState(null)
  const [name, setName]         = useState('')
  const [portal, setPortal]     = useState(null)   // { id, status, token, ... }
  const [link, setLink]         = useState('')
  const [busy, setBusy]         = useState(false)
  const [error, setError]       = useState(null)
  const [copied, setCopied]     = useState(false)

  const [recipients, setRecipients] = useState(null)     // null = loading
  const [gcContacts, setGcContacts] = useState(null)      // the GC org's contacts with an email on file
  const [checked, setChecked]       = useState({})
  const [staged, setStaged]         = useState([])
  const [manualName, setManualName] = useState('')
  const [manualEmail, setManualEmail] = useState('')
  const [sending, setSending]       = useState(false)

  const reloadRecipients = useCallback(() => {
    if (!portal) return
    listRecipients(portal.id).then(setRecipients).catch(() => setRecipients([]))
  }, [portal])

  useEffect(() => {
    function onOpen(e) {
      const d = e.detail || {}
      if (!d.contractId) return
      setCid(d.contractId)
      setName(d.contractorName || 'the contractor')
      setPortal(null); setLink(''); setError(null); setCopied(false); setBusy(true); setOpen(true)
      setRecipients(null); setGcContacts(null); setChecked({}); setStaged([]); setManualName(''); setManualEmail('')
      getOrCreatePortal(d.contractId)
        .then(r => { setPortal(r.portal); setLink(r.link) })
        .catch(ex => setError(ex.message || 'Failed to load portal link'))
        .finally(() => setBusy(false))
    }
    window.addEventListener('open-contractor-portal-link', onOpen)
    return () => window.removeEventListener('open-contractor-portal-link', onOpen)
  }, [])

  useEffect(() => { if (portal) reloadRecipients() }, [portal, reloadRecipients])

  // The contract's linked GC Organization's Contacts — resolved via construction_contracts.
  // contractor_airtable_id (set at Award, or via the "Link to Organization" field on the contract
  // editor). No org link yet → empty list, not an error; recipients can still be added manually.
  useEffect(() => {
    if (!open || !contractId) return
    setGcContacts(null)
    getContract(contractId)
      .then(async ({ contract }) => {
        const orgId = contract && contract.contractor_airtable_id
        if (!orgId) { setGcContacts([]); return }
        const org = await getOrganizationDetail(orgId)
        const contactIds = (org && org.fields && org.fields['Contacts']) || []
        if (!contactIds.length) { setGcContacts([]); return }
        const [names, emails] = await Promise.all([getContactNamesByIds(contactIds), getContactEmailsByIds(contactIds)])
        setGcContacts(contactIds.map(id => ({ contactId: id, name: names[id] || 'Unknown', email: emails[id] || '' })).filter(c => c.email))
      })
      .catch(() => setGcContacts([]))
  }, [open, contractId])

  useEffect(() => {
    if (open) { document.body.style.overflow = 'hidden'; return () => { document.body.style.overflow = '' } }
  }, [open])

  useEffect(() => {
    function onKey(e) { if (e.key === 'Escape' && open) setOpen(false) }
    document.addEventListener('keydown', onKey)
    return () => document.removeEventListener('keydown', onKey)
  }, [open])

  if (!open) return null

  function copy() {
    if (!link) return
    navigator.clipboard?.writeText(link).then(() => { setCopied(true); setTimeout(() => setCopied(false), 1500) }).catch(() => {})
  }
  function toggleStatus() {
    if (!contractId || !portal) return
    const next = portal.status === 'disabled' ? 'active' : 'disabled'
    setBusy(true)
    setPortalStatus(contractId, next)
      .then(r => setPortal(r.portal))
      .catch(ex => setError(ex.message || 'Failed'))
      .finally(() => setBusy(false))
  }
  async function regenerate() {
    if (!contractId) return
    if (!(await confirmDialog({ title: 'Rotate the shared link?', message: 'Anyone still using the original shared link+PIN loses access immediately. This never affects any individual recipient below.', confirmLabel: 'Rotate link', danger: true }))) return
    setBusy(true)
    regenerateToken(contractId)
      .then(r => { setPortal(r.portal); setLink(r.link); setCopied(false) })
      .catch(ex => setError(ex.message || 'Failed'))
      .finally(() => setBusy(false))
  }

  function addManual() {
    const n = manualName.trim(), e = manualEmail.trim()
    if (!n || !EMAIL_RE.test(e)) return
    setStaged(s => s.concat([{ name: n, email: e }]))
    setManualName(''); setManualEmail('')
  }

  const activeEmails = new Set((recipients || []).filter(r => r.status === 'active').map(r => r.email.toLowerCase()))
  const toSend = [
    ...Object.keys(checked).filter(id => checked[id]).map(id => {
      const c = (gcContacts || []).find(x => x.contactId === id)
      return c ? { name: c.name, email: c.email, contactId: c.contactId } : null
    }).filter(Boolean),
    ...staged,
  ].filter(r => !activeEmails.has(r.email.toLowerCase()))

  async function handleSend() {
    if (!toSend.length || sending || !portal) return
    setSending(true); setError(null)
    try {
      await addRecipients(portal.id, toSend)
      setChecked({}); setStaged([])
      reloadRecipients()
    } catch (ex) {
      setError(ex.message || 'Failed to send')
    } finally {
      setSending(false)
    }
  }

  async function handleRevoke(r) {
    if (!(await confirmDialog({ title: 'Revoke access?', message: `${r.name}'s link and access code stop working immediately. This doesn't affect anyone else on this contract.`, confirmLabel: 'Revoke', danger: true }))) return
    await revokeRecipient(r.id); reloadRecipients()
  }
  async function handleReactivate(r) { await reactivateRecipient(r.id); reloadRecipients() }
  async function handleDelete(r) {
    if (!(await confirmDialog({ title: 'Remove from list?', message: `Permanently removes ${r.name} (${r.email}) from this contract's recipient list. Their past submissions, if any, are kept but no longer linked to a name. This can't be undone.`, confirmLabel: 'Remove', danger: true }))) return
    try { await deleteRecipient(r.id); reloadRecipients() } catch (ex) { setError(ex.message || 'Failed to remove') }
  }
  async function handleResend(r) {
    try { await resendRecipientAccess(r.id) } catch (ex) { setError(ex.message || 'Failed to send') }
  }
  async function handleRegenerate(r) {
    if (!(await confirmDialog({ title: 'Regenerate their access?', message: `${r.name}'s CURRENT link and access code stop working immediately and a new one is emailed to them. Use this if they lost access, or if their link may have been shared with someone else.`, confirmLabel: 'Regenerate & resend' }))) return
    try { await regenerateRecipientAccess(r.id); reloadRecipients() } catch (ex) { setError(ex.message || 'Failed to regenerate') }
  }

  const disabled = portal && portal.status === 'disabled'
  const btn = { padding: '6px 14px', fontFamily: '"DM Sans",sans-serif', fontSize: 12, borderRadius: 4, cursor: 'pointer' }

  return createPortal(
    <div style={{ position: 'fixed', inset: 0, zIndex: 600, background: 'rgba(0,0,0,0.35)', display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 20 }}>
      <div data-modal-id="ContractorPortalLinkModal" style={{ background: 'var(--aj-bg-elevated)', border: '0.5px solid var(--aj-border)', borderRadius: 8, boxShadow: '0 12px 36px rgba(0,0,0,0.16)', width: 560, maxWidth: '94vw', maxHeight: '88vh', display: 'flex', flexDirection: 'column' }}>
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '16px 20px', borderBottom: '0.5px solid var(--aj-border)', flexShrink: 0 }}>
          <span style={{ fontFamily: '"DM Sans",sans-serif', fontSize: 14, fontWeight: 700, color: 'var(--aj-text)' }}>Contractor Portal Access — {name}</span>
          <button onClick={() => setOpen(false)} style={{ background: 'none', border: 'none', cursor: 'pointer', color: 'var(--aj-text-mid)', fontSize: 18, lineHeight: 1, padding: 2 }}>×</button>
        </div>

        <div style={{ padding: '16px 20px', overflowY: 'auto', display: 'flex', flexDirection: 'column', gap: 16 }}>
          {/* Recipients — primary flow */}
          <div>
            <label style={labelStyle}>Recipients</label>
            {recipients === null && <div style={{ fontFamily: '"DM Sans",sans-serif', fontSize: 12, color: 'var(--aj-text-muted)' }}>Loading…</div>}
            {recipients && recipients.length === 0 && (
              <div style={{ fontFamily: '"DM Sans",sans-serif', fontSize: 12, color: 'var(--aj-text-muted)', fontStyle: 'italic' }}>
                No one has been sent access yet — add recipients below.
              </div>
            )}
            {recipients && recipients.length > 0 && (
              <div style={{ display: 'flex', flexDirection: 'column', border: '0.5px solid var(--aj-border)', borderRadius: 6, overflow: 'hidden' }}>
                {recipients.map(r => (
                  <RecipientRow key={r.id} r={r}
                    onResend={() => handleResend(r)} onRegenerate={() => handleRegenerate(r)}
                    onRevoke={() => handleRevoke(r)} onReactivate={() => handleReactivate(r)}
                    onDelete={() => handleDelete(r)} />
                ))}
              </div>
            )}
          </div>

          <hr style={{ border: 'none', borderTop: '0.5px solid var(--aj-border)', margin: 0 }} />

          {/* Add recipients */}
          <div>
            <label style={labelStyle}>Add recipients</label>
            {gcContacts === null && <div style={{ fontFamily: '"DM Sans",sans-serif', fontSize: 12, color: 'var(--aj-text-muted)' }}>Loading the GC's contacts…</div>}
            {gcContacts && gcContacts.length === 0 && (
              <div style={{ fontFamily: '"DM Sans",sans-serif', fontSize: 11.5, color: 'var(--aj-text-muted)', marginBottom: 8 }}>
                No linked organization contacts found — link the GC's Organization on the contract, or add a name + email manually below.
              </div>
            )}
            {gcContacts && gcContacts.length > 0 && (
              <div style={{ display: 'flex', flexDirection: 'column', gap: 4, marginBottom: 10 }}>
                {gcContacts.map(c => {
                  const already = activeEmails.has(c.email.toLowerCase())
                  return (
                    <label key={c.contactId} style={{ display: 'flex', alignItems: 'center', gap: 8, fontFamily: '"DM Sans",sans-serif', fontSize: 12.5, color: already ? 'var(--aj-text-muted)' : 'var(--aj-text)', cursor: already ? 'default' : 'pointer' }}>
                      <input type="checkbox" disabled={already} checked={!!checked[c.contactId]} onChange={e => setChecked(prev => ({ ...prev, [c.contactId]: e.target.checked }))} />
                      {c.name} <span style={{ color: 'var(--aj-text-muted)', fontSize: 11 }}>{c.email}</span>
                      {already && <span style={{ fontSize: 10, color: '#1D9E75' }}>· already has access</span>}
                    </label>
                  )
                })}
              </div>
            )}

            <div style={{ display: 'flex', gap: 6, marginBottom: staged.length ? 8 : 0 }}>
              <input style={{ ...inputStyle, flex: '0 1 150px' }} value={manualName} onChange={e => setManualName(e.target.value)} placeholder="Name" />
              <input type="email" style={{ ...inputStyle, flex: 1 }} value={manualEmail} onChange={e => setManualEmail(e.target.value)} placeholder="email@example.com"
                onKeyDown={e => { if (e.key === 'Enter') addManual() }} />
              <button onClick={addManual} disabled={!manualName.trim() || !EMAIL_RE.test(manualEmail.trim())} style={smallBtn}>+ Add</button>
            </div>

            {staged.length > 0 && (
              <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6, marginTop: 8 }}>
                {staged.map((s, i) => (
                  <span key={i} style={{ display: 'inline-flex', alignItems: 'center', gap: 6, padding: '4px 8px', background: 'var(--aj-bg-subtle)', border: '0.5px solid var(--aj-border-mid)', borderRadius: 20, fontFamily: '"DM Sans",sans-serif', fontSize: 11.5, color: 'var(--aj-text)' }}>
                    {s.name} <span style={{ color: 'var(--aj-text-muted)' }}>{s.email}</span>
                    <button onClick={() => setStaged(list => list.filter((_, j) => j !== i))} style={{ background: 'none', border: 'none', cursor: 'pointer', color: 'var(--aj-text-muted)', fontSize: 13, lineHeight: 1, padding: 0 }}>×</button>
                  </span>
                ))}
              </div>
            )}

            <div style={{ display: 'flex', justifyContent: 'flex-end', marginTop: 10 }}>
              <button onClick={handleSend} disabled={!toSend.length || sending} style={{ padding: '7px 16px', fontFamily: '"DM Sans",sans-serif', fontSize: 12, fontWeight: 600, background: (toSend.length && !sending) ? '#07272D' : 'var(--aj-bg-muted)', color: (toSend.length && !sending) ? '#fff' : 'var(--aj-text-muted)', border: 'none', borderRadius: 4, cursor: (toSend.length && !sending) ? 'pointer' : 'default' }}>
                {sending ? 'Sending…' : `Send access${toSend.length ? ' (' + toSend.length + ')' : ''}`}
              </button>
            </div>
          </div>

          <hr style={{ border: 'none', borderTop: '0.5px solid var(--aj-border)', margin: 0 }} />

          {/* Shared link (legacy) */}
          <div>
            <label style={labelStyle}>Shared link (legacy)</label>
            <div style={{ fontFamily: '"DM Sans",sans-serif', fontSize: 11.5, lineHeight: 1.5, color: 'var(--aj-text-mid)', marginBottom: 8 }}>
              Works for anyone who already has it, but is no longer issued to anyone new — use Recipients above instead.
            </div>
            {busy && !portal && <div style={{ fontFamily: '"DM Sans",sans-serif', fontSize: 12, color: 'var(--aj-text-muted)' }}>Loading…</div>}
            {portal && (
              <>
                <div style={{ display: 'flex', gap: 8, alignItems: 'center', marginBottom: 6 }}>
                  <input readOnly value={link}
                    style={{ flex: 1, padding: '7px 10px', fontFamily: '"DM Mono",monospace', fontSize: 11.5, color: disabled ? 'var(--aj-text-muted)' : 'var(--aj-text)', background: 'var(--aj-bg-subtle)', border: '0.5px solid var(--aj-border-mid)', borderRadius: 4, textDecoration: disabled ? 'line-through' : 'none' }}
                    onFocus={e => e.target.select()} />
                  <button onClick={copy} disabled={disabled} style={{ ...btn, fontWeight: 600, background: disabled ? 'var(--aj-bg-muted)' : '#07272D', color: disabled ? 'var(--aj-text-muted)' : '#fff', border: 'none' }}>
                    {copied ? 'Copied' : 'Copy'}
                  </button>
                </div>
                <div style={{ fontFamily: '"DM Sans",sans-serif', fontSize: 11, color: disabled ? '#c0392b' : '#1D9E75', marginBottom: 12 }}>
                  {disabled ? 'Disabled — the link will not load.' : 'Active'}
                </div>

                <div style={{ display: 'flex', justifyContent: 'space-between', gap: 8 }}>
                  <button onClick={regenerate} disabled={busy} style={{ ...btn, background: 'none', border: '0.5px solid var(--aj-border-mid)', color: 'var(--aj-text)' }}>Rotate link</button>
                  <button onClick={toggleStatus} disabled={busy} style={{ ...btn, background: 'none', border: '0.5px solid ' + (disabled ? 'rgba(29,158,117,0.5)' : 'rgba(192,57,43,0.5)'), color: disabled ? '#1D9E75' : '#c0392b' }}>
                    {disabled ? 'Re-enable' : 'Disable link'}
                  </button>
                </div>
              </>
            )}
          </div>

          {error && <div style={{ fontFamily: '"DM Sans",sans-serif', fontSize: 11, color: '#c0392b' }}>{error}</div>}
        </div>

        <div style={{ display: 'flex', justifyContent: 'flex-end', padding: '14px 20px', borderTop: '0.5px solid var(--aj-border)', flexShrink: 0 }}>
          <button onClick={() => setOpen(false)} style={{ padding: '7px 16px', fontFamily: '"DM Sans",sans-serif', fontSize: 12, background: 'none', border: '0.5px solid var(--aj-border-mid)', borderRadius: 4, cursor: 'pointer', color: 'var(--aj-text)' }}>
            Close
          </button>
        </div>
      </div>
    </div>,
    document.body
  )
}

function RecipientRow({ r, onResend, onRegenerate, onRevoke, onReactivate, onDelete }) {
  const menu = useActionMenu()
  const revoked = r.status === 'revoked'
  return (
    <div className="aj-recip-row" style={{ display: 'flex', alignItems: 'center', gap: 10, padding: '9px 12px', borderBottom: '0.5px solid var(--aj-border)' }}>
      {/* Same scoped ⋯ reveal override as SendJourneyAccessModal.jsx — this modal's plain flex
          rows don't carry .wf-row, so ActionMenuButton's hover-reveal needs a local rule. */}
      <style>{`
        .aj-recip-row .wf-menu-btn { opacity: 0.4; }
        .aj-recip-row:hover .wf-menu-btn { opacity: 1; }
      `}</style>
      <div style={{ flex: 1, minWidth: 0 }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
          <span style={{ fontFamily: '"DM Sans",sans-serif', fontSize: 12.5, fontWeight: 600, color: 'var(--aj-text)' }}>{r.name}</span>
          {revoked && <span className="wf-badge wf-badge--gray">Revoked</span>}
        </div>
        <div style={{ fontFamily: '"DM Mono",monospace', fontSize: 10.5, color: 'var(--aj-text-muted)', marginTop: 1 }}>
          {r.email} · {fmtWhen(r.last_opened_at)}
        </div>
      </div>
      <ActionMenuButton menu={menu} />
      <ActionMenu menu={menu}>
        {!revoked && <ActionMenuItem onClick={onResend}>Resend access</ActionMenuItem>}
        {!revoked && <ActionMenuItem onClick={onRegenerate}>Regenerate & resend</ActionMenuItem>}
        <ActionMenuDivider />
        {revoked
          ? <>
              <ActionMenuItem onClick={onReactivate}>Reactivate</ActionMenuItem>
              <ActionMenuItem onClick={onDelete} danger>Remove from list</ActionMenuItem>
            </>
          : <ActionMenuItem onClick={onRevoke} danger>Revoke</ActionMenuItem>}
      </ActionMenu>
    </div>
  )
}
